import React from 'react';
import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  ConstellationSystem,
  KspaceIntel,
  NearbySkyhook,
  ScoutConnection,
  SECURITY_BAND_LABEL,
  fetchKspaceIntel,
  formatMinutes,
} from '../api/kspace';
import ConsolePanel from './ConsolePanel';
import LcarsRule from './LcarsRule';
import PanelHeader from './PanelHeader';
import StatusBadge from './StatusBadge';
import { C, FONT, frameSx, glow, hexToRgba, lcarsLabelSx } from '../theme/tokens';

/** Security band drives the whole page's temperature: highsec reads calm,
 * lowsec cautious, nullsec hostile. Same danger-scale logic `classColor` uses
 * for wormhole classes, applied to the other half of the universe. */
export const bandColor = (band: KspaceIntel['securityBand']): string =>
  band === 'highsec' ? C.green : band === 'lowsec' ? C.gold : band === 'nullsec' ? C.red : C.textDim;

/**
 * The K-space dashboard.
 *
 * J-space and K-space answer different questions, so they get different
 * panels rather than one compromise layout: a wormhole scout asks about
 * statics, effects and Sleeper sites, while someone in known space asks how
 * far the nearest hub is, who owns the space, who is shooting, and whether
 * there is a raidable skyhook in range.
 *
 * Everything here is served by one `/api/intel/kspace/:id` call reading local
 * tables — the stargate graph plus two ESI mirrors — so none of it costs an
 * ESI round trip per view.
 */
const KspacePanel: React.FC<{ solarSystemId: number; systemName: string }> = ({ solarSystemId, systemName }) => {
  const q = useQuery({
    queryKey: ['kspace', solarSystemId],
    queryFn: () => fetchKspaceIntel(solarSystemId),
    staleTime: 60_000,
  });

  if (q.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress sx={{ color: C.wormhole }} />
      </Box>
    );
  }
  if (q.isError || !q.data) {
    return (
      <ConsolePanel title="Unavailable" accent={C.red} corners={false} sx={frameSx(C.red)}>
        <Typography sx={{ color: C.textDim }}>Couldn&rsquo;t load known-space intel for this system.</Typography>
      </ConsolePanel>
    );
  }

  const k = q.data;
  const accent = bandColor(k.securityBand);

  return (
    <Stack spacing={3}>
      <Box>
        <PanelHeader label="System" accent={accent} dense sx={{ mb: 0.75 }} />
        <LcarsRule accent={accent} sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <ConsolePanel title="Space" accent={accent} corners={false} sx={frameSx(accent)}>
            <Stack spacing={1.25}>
              <Row label="Security">
                <Stack direction="row" spacing={1} alignItems="center">
                  <StatusBadge label={SECURITY_BAND_LABEL[k.securityBand]} accent={accent} />
                  <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.85rem', color: C.text }}>
                    {k.securityStatus !== null ? k.securityStatus.toFixed(2) : '—'}
                  </Typography>
                </Stack>
              </Row>
              <Row label="Sovereignty">
                {k.sovereignty ? (
                  <Stack spacing={0.25}>
                    {k.sovereignty.allianceName && (
                      <Typography sx={{ color: C.text, fontSize: '0.88rem' }}>{k.sovereignty.allianceName}</Typography>
                    )}
                    {k.sovereignty.corporationName && (
                      <Typography sx={{ color: C.textDim, fontFamily: FONT.sub, fontWeight: 500, fontSize: '0.82rem' }}>
                        {k.sovereignty.corporationName}
                      </Typography>
                    )}
                    {!k.sovereignty.allianceName && !k.sovereignty.corporationName && k.sovereignty.factionName && (
                      <Typography sx={{ color: C.text, fontSize: '0.88rem' }}>{k.sovereignty.factionName}</Typography>
                    )}
                  </Stack>
                ) : (
                  <Typography sx={{ color: C.textDim, fontSize: '0.85rem' }}>Unclaimed</Typography>
                )}
              </Row>
            </Stack>
          </ConsolePanel>

          {/* The jump box. Distances come from a local BFS over the SDE's
              stargate graph and are memoized per system, so this is instant
              and costs no ESI calls. */}
          <ConsolePanel title="Jumps to Hubs" accent={C.cyan} corners={false} sx={frameSx(C.cyan)}>
            {k.hubs.every((h) => h.jumps === null) ? (
              <Typography sx={{ color: C.textDim }}>No gate route from here.</Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                {k.hubs.map((h) => (
                  <Box key={h.key} sx={{ textAlign: 'center' }}>
                    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.64rem'), mb: 0.5 }}>{h.name}</Typography>
                    <Typography
                      sx={{
                        fontFamily: FONT.mono,
                        fontSize: '1.5rem',
                        lineHeight: 1,
                        color: h.jumps === null ? C.textDim : h.jumps === 0 ? C.green : C.text,
                        textShadow: h.jumps !== null && h.jumps <= 5 ? glow(C.cyan, 12, 0.5) : 'none',
                      }}
                    >
                      {h.jumps ?? '—'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </ConsolePanel>
        </Box>
      </Box>

      <Box>
        <PanelHeader label="Threat" accent={C.red} dense sx={{ mb: 0.75 }} />
        <LcarsRule accent={C.red} sx={{ mb: 1.5 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <ThreatAnalysis intel={k} systemName={systemName} />
          <ConstellationActivity systems={k.constellation.systems} totals={k.constellation.totals} />
        </Box>
      </Box>

      {(k.nearbySkyhooks.length > 0 || k.nearestScoutConnection) && (
        <Box>
          <PanelHeader label="Opportunity" accent={C.gold} dense sx={{ mb: 0.75 }} />
          <LcarsRule accent={C.gold} sx={{ mb: 1.5 }} />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: k.nearbySkyhooks.length > 0 && k.nearestScoutConnection ? { xs: '1fr', md: '1fr 1fr' } : '1fr',
              gap: 2,
              alignItems: 'start',
            }}
          >
            {k.nearestScoutConnection && <ScoutConnectionCard connection={k.nearestScoutConnection} />}
            {k.nearbySkyhooks.length > 0 && <NearbySkyhooks skyhooks={k.nearbySkyhooks} />}
          </Box>
        </Box>
      )}
    </Stack>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.68rem'), pt: 0.4, flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>{children}</Box>
  </Stack>
);

/** The shared judgement both the single-system and constellation-wide threat
 * panels render: turns raw last-hour kill counts into the read a pilot
 * actually wants — is anyone hunting here right now. Deliberately
 * conservative, with its reasoning always shown, because a confident-looking
 * verdict from one hour of data would be worse than none. Ship+pod kills
 * mean players are dying to players; NPC kills alone mean someone is
 * ratting, which is an opportunity as much as a threat.
 *
 * `scope` only changes the sentence's subject ("this system" vs "this
 * constellation") — the thresholds stay identical. A constellation summing
 * several systems' kills is more likely to cross the "Hot" bar than any one
 * quiet system in it, and that is the honest signal: a constellation showing
 * Hot means shooting is happening *somewhere* in it, not that every system
 * is dangerous — ConstellationActivity's per-system bars right below settle
 * exactly where. */
function threatVerdict(
  kills: { shipKills: number; podKills: number; npcKills: number },
  scope: 'system' | 'constellation',
): { level: string; accent: string; verdict: string } {
  const pvp = kills.shipKills + kills.podKills;
  const here = scope === 'system' ? 'here' : 'in this constellation';
  const onGrid = scope === 'system' ? 'on grid' : 'somewhere in it';

  if (pvp >= 10) return { level: 'Hot', accent: C.red, verdict: `Sustained PvP in the last hour. Assume a hostile gang is ${onGrid}.` };
  if (pvp >= 3) return { level: 'Active', accent: C.gold, verdict: `Players are dying ${here} right now. Scout before committing.` };
  if (pvp > 0) return { level: 'Contested', accent: C.gold, verdict: `At least one player kill ${here} in the last hour.` };
  if (kills.npcKills >= 30)
    return { level: 'Ratting', accent: C.cyan, verdict: `Heavy NPC activity and no player kills ${here} — someone is farming, undefended so far.` };
  if (kills.npcKills > 0) return { level: 'Quiet', accent: C.green, verdict: `Some ratting ${here}, no player kills in the last hour.` };
  return { level: 'Silent', accent: C.green, verdict: `Nothing died ${here} in the last hour.` };
}

/**
 * Threat analysis: turns the last hour's raw kill counts into the judgement a
 * pilot actually wants — is anyone hunting here right now.
 */
const ThreatAnalysis: React.FC<{ intel: KspaceIntel; systemName: string }> = ({ intel, systemName }) => {
  const kills = intel.kills ?? { shipKills: 0, podKills: 0, npcKills: 0 };
  const { level, accent, verdict } = threatVerdict(kills, 'system');

  return (
    <ConsolePanel
      title="Threat Analysis"
      accent={accent}
      corners={false}
      sx={frameSx(accent)}
      action={<StatusBadge label={level} accent={accent} />}
    >
      <Stack spacing={1.5}>
        <Typography sx={{ color: C.text, fontSize: '0.9rem' }}>{verdict}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
          <KillStat label="Ships" value={kills.shipKills} accent={kills.shipKills > 0 ? C.red : C.textDim} />
          <KillStat label="Pods" value={kills.podKills} accent={kills.podKills > 0 ? C.red : C.textDim} />
          <KillStat label="NPCs" value={kills.npcKills} accent={kills.npcKills > 0 ? C.cyan : C.textDim} />
        </Box>
        {/* A full sentence — body voice, not the data face. */}
        <Typography sx={{ color: C.textDim, fontFamily: FONT.body, fontSize: '0.78rem', lineHeight: 1.5 }}>
          ESI last-hour totals for {systemName}. Empty means no kills, not no data.
        </Typography>
      </Stack>
    </ConsolePanel>
  );
};

const KillStat: React.FC<{ label: string; value: number; accent: string }> = ({ label, value, accent }) => (
  <Box sx={{ textAlign: 'center', py: 0.5, borderRadius: '8px', bgcolor: hexToRgba(accent, 0.06) }}>
    <Typography sx={{ fontFamily: FONT.mono, fontSize: '1.3rem', lineHeight: 1, color: accent }}>{value}</Typography>
    <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.6rem'), mt: 0.5 }}>{label}</Typography>
  </Box>
);

/**
 * Where the shooting is across the whole constellation — the "where is ratting
 * happening" view. Each system gets a bar scaled against the busiest one, so
 * the shape of the activity reads before any number does.
 */
const ConstellationActivity: React.FC<{
  systems: ConstellationSystem[];
  totals: { shipKills: number; podKills: number; npcKills: number };
}> = ({ systems, totals }) => {
  const max = Math.max(1, ...systems.map((s) => s.npcKills + s.shipKills));
  const active = systems.filter((s) => s.npcKills + s.shipKills + s.podKills > 0);
  const { level, accent, verdict } = threatVerdict(totals, 'constellation');

  return (
    <ConsolePanel
      title="Constellation Activity"
      accent={C.cyan}
      corners={false}
      sx={frameSx(C.cyan)}
      action={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.66rem') }}>
            {totals.npcKills} npc &middot; {totals.shipKills + totals.podKills} pvp
          </Typography>
          <StatusBadge label={level} accent={accent} />
        </Stack>
      }
    >
      {/* Same verdict logic as the single-system Threat Analysis panel,
          applied to the constellation's summed kills — see threatVerdict.
          Covers the "nothing died" case on its own, so no separate empty
          message is needed below it. */}
      <Typography sx={{ color: C.text, fontSize: '0.9rem', mb: 1.5 }}>{verdict}</Typography>
      {active.length > 0 && (
        <Stack spacing={0.9}>
          {systems.slice(0, 10).map((s) => {
            const total = s.npcKills + s.shipKills;
            const pvp = s.shipKills + s.podKills;
            return (
              <Box key={s.solarSystemId}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                  <Typography
                    component={RouterLink}
                    to={`/system/${encodeURIComponent(s.name)}`}
                    sx={{
                      fontFamily: FONT.mono,
                      fontSize: '0.8rem',
                      color: pvp > 0 ? C.red : C.text,
                      textDecoration: 'none',
                      '&:hover': { color: C.textBright },
                    }}
                  >
                    {s.name}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.72rem', color: C.textDim, flexShrink: 0 }}>
                    {s.npcKills} npc{pvp > 0 ? ` · ${pvp} pvp` : ''}
                  </Typography>
                </Stack>
                {/* Two-tone bar: NPC share in cyan, PvP share in red, so
                    "busy with rats" and "busy with players" never look the
                    same at a glance. */}
                <Box sx={{ display: 'flex', height: 4, borderRadius: 999, overflow: 'hidden', bgcolor: hexToRgba(C.wormhole, 0.08), mt: 0.4 }}>
                  <Box sx={{ width: `${(s.npcKills / max) * 100}%`, bgcolor: hexToRgba(C.cyan, 0.75) }} />
                  <Box sx={{ width: `${(s.shipKills / max) * 100}%`, bgcolor: C.red }} />
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </ConsolePanel>
  );
};

/** Raidable skyhooks within range, closest first. This is the whole reason
 * the skyhook feed has no page of its own — it is only useful relative to
 * where you already are. */
const NearbySkyhooks: React.FC<{ skyhooks: NearbySkyhook[] }> = ({ skyhooks }) => (
  <ConsolePanel
    title="Raidable Skyhooks Nearby"
    accent={C.gold}
    corners={false}
    sx={frameSx(C.gold)}
    action={<Typography sx={{ ...lcarsLabelSx(C.textDim, '0.66rem') }}>within 15 jumps</Typography>}
  >
    <Stack spacing={0}>
      {skyhooks.map((s) => {
        const accent = s.open ? C.red : C.gold;
        return (
          <Stack
            key={s.planetId}
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ py: 0.9, borderBottom: `1px solid ${C.hairline}`, '&:last-of-type': { borderBottom: 'none', pb: 0 } }}
          >
            <Box sx={{ minWidth: 44, textAlign: 'center', flexShrink: 0 }}>
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '1rem', color: s.jumps <= 5 ? C.green : C.text, lineHeight: 1 }}>
                {s.jumps}
              </Typography>
              <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.55rem') }}>jumps</Typography>
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '0.86rem' }}>
                <Box
                  component={RouterLink}
                  to={`/system/${encodeURIComponent(s.systemName)}`}
                  sx={{ color: C.text, fontWeight: 600, textDecoration: 'none', '&:hover': { color: C.textBright } }}
                >
                  {s.systemName}
                </Box>
                <Box component="span" sx={{ color: C.textDim }}> &middot; {s.planetName}</Box>
              </Typography>
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.textDim }}>
                {s.regionName ?? 'Unknown region'}
              </Typography>
            </Box>
            <Box
              sx={{
                flexShrink: 0,
                px: 1,
                py: 0.25,
                borderRadius: 999,
                border: `1px solid ${hexToRgba(accent, 0.55)}`,
                bgcolor: hexToRgba(accent, 0.08),
              }}
            >
              <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.74rem', color: accent }}>
                {s.open ? `${formatMinutes(s.minutesRemaining)} left` : `in ${formatMinutes(s.minutesUntilOpen)}`}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  </ConsolePanel>
);

/** The nearest live Thera/Turnur wormhole, from the corp's `skyhook` tool's
 * own EVE Scout integration (services/eveScoutPoller.ts there) — a K-space
 * system's own shortcut-awareness, mirroring what that tool already gives a
 * skyhook. A hole can die mid-session, hence the "as of" framing rather than
 * a bare fact. */
const ScoutConnectionCard: React.FC<{ connection: ScoutConnection }> = ({ connection: s }) => (
  <ConsolePanel
    title="Nearby Wormhole"
    accent={C.wormhole}
    corners={false}
    sx={frameSx(C.wormhole)}
    action={<Typography sx={{ ...lcarsLabelSx(C.textDim, '0.66rem') }}>EVE Scout</Typography>}
  >
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Box sx={{ minWidth: 44, textAlign: 'center', flexShrink: 0 }}>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '1rem', color: s.jumps <= 5 ? C.green : C.text, lineHeight: 1 }}>
          {s.jumps}
        </Typography>
        <Typography sx={{ ...lcarsLabelSx(C.textDim, '0.55rem') }}>jumps</Typography>
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: '0.86rem' }}>
          <Box
            component={RouterLink}
            to={`/system/${encodeURIComponent(s.exitSystemName)}`}
            sx={{ color: C.text, fontWeight: 600, textDecoration: 'none', '&:hover': { color: C.textBright } }}
          >
            {s.exitSystemName}
          </Box>
          <Box component="span" sx={{ color: C.textDim }}>
            {' '}
            &rarr; {s.hub}
          </Box>
        </Typography>
        <Typography sx={{ fontFamily: FONT.mono, fontSize: '0.68rem', color: C.textDim }}>
          {s.whType ?? 'unknown type'}
          {s.remainingHours !== null && ` · ~${Math.max(0, Math.round(s.remainingHours))}h left`}
        </Typography>
      </Box>
    </Stack>
  </ConsolePanel>
);

export default KspacePanel;
